import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  RadioGroup,
  Radio,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { XlsService, UploadedFileInfo, AtomicUploadResponse } from '../../services/xlsService';
import { formatUTCForDisplay } from '../../utils/dateUtils';
import { ToastService } from '../../services/toastService';
import { useAuth } from '../../hooks/useAuth';
import { Delete as DeleteIcon } from '@mui/icons-material';
import ConfirmationPopover from '../../components/ConfirmationPopover/ConfirmationPopover';
import { useConfirmationPopover } from '../../hooks/useConfirmationPopover';

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: string;
  uploadedFileType: 'Raw' | 'Formatted';
  uploadedFrom: number; // 1 = Client, 2 = Admin
  uploadedByName: string;
  uploadedByEmail: string;
  status: string;
  downloadUrl: string;
  uploadedAt: string;
  createdAt: string;
  updatedAt: string;
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  rawUploadId?: string; // For formatted files - ID of linked raw upload
  formattedUploadId?: string; // For raw files - ID of linked formatted upload
  relatedRawFileId?: string; // Legacy field for backward compatibility
  processingStatus?: 'unprocessed' | 'processed' | 'failed';
}

interface UploadedFilesModalProps {
  open: boolean;
  onClose: () => void;
  vesselName: string;
  vesselImo: string;
  vesselId: string;
}

// Default step to show when an error occurs (Parse & Validate step)
const DEFAULT_ERROR_STEP = 1;

export const UploadedFilesModal = ({
  open,
  onClose,
  vesselName,
  vesselImo,
  vesselId,
}: UploadedFilesModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedRawFileId, setSelectedRawFileId] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveComments, setApproveComments] = useState('');
  const [fileToApprove, setFileToApprove] = useState<UploadedFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState(''); // Will be 'fileOnly' or 'fileAndVoyages'

  // Upload confirmation popover
  const {
    anchorEl: uploadAnchorEl,
    isOpen: isUploadOpen,
    openConfirmation: openUploadConfirmation,
    closeConfirmation: closeUploadConfirmation,
  } = useConfirmationPopover();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Stepper state
  const [activeStep, setActiveStep] = useState(-1);
  const [uploadResult, setUploadResult] = useState<AtomicUploadResponse | null>(null);
  const [showProgress, setShowProgress] = useState(false);

  const { user } = useAuth();
  const theme = useTheme();

  const steps = ['File Upload', 'Parse & validate file', 'Voyage creation'];

  useEffect(() => {
    if (open && vesselId) {
      fetchUploadedFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vesselId]);

  const fetchUploadedFiles = async () => {
    try {
      setLoading(true);
      const data = await XlsService.getVesselUploadsList();

      // Find the vessel's uploads
      const vesselData = data.find((v) => v.vesselId === vesselId);

      if (vesselData && vesselData.uploadedFileInfo) {
        // Transform API data to component format
        const files: UploadedFile[] = vesselData.uploadedFileInfo.map((file: UploadedFileInfo) => ({
          id: file.id,
          fileName: file.gcsInfo.name,
          fileSize: formatFileSize(file.gcsInfo.size),
          uploadedFileType: file.uploadedType === 1 ? 'Raw' : 'Formatted',
          uploadedFrom: file.uploadedFrom,
          uploadedByName: file.uploadedByName || '',
          uploadedByEmail: file.uploadedByEmail || '',
          status: file.status,
          downloadUrl: file.gcsInfo.downloadUrl,
          uploadedAt: file.createdAt,
          createdAt: file.createdAt,
          updatedAt: file.updatedAt,
          totalRows: file.totalRows || 0,
          successfulRows: file.successfulRows || 0,
          failedRows: file.failedRows || 0,
          rawUploadId: file.rawUploadId,
          formattedUploadId: file.formattedUploadId,
          relatedRawFileId: file.rawUploadId,
          processingStatus: 'unprocessed',
        }));
        setUploadedFiles(files);
      } else {
        setUploadedFiles([]);
      }
    } catch (error) {
      console.error('Failed to fetch uploaded files:', error);
      setUploadedFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded':
        return 'info';
      case 'processing':
        return 'warning';
      case 'approved':
        return 'success';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Downloads a file using GCS signed URL.
   * Updated to fetch fresh signed URL from backend to avoid expiration issues.
   */
  const handleDownload = async (file: UploadedFile) => {
    try {
      // Fetch fresh signed URL from backend
      const { downloadUrl, fileName } = await XlsService.getFreshDownloadUrl(file.id);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName; // Set the filename for download

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      ToastService.success(`Downloading "${fileName}"`, 'Download Started');
    } catch (error) {
      console.error('Failed to download file:', error);
      ToastService.error(
        'Failed to download file. The file may no longer be available.',
        'Download Failed'
      );
    }
  };

  const handleApproveConfirm = async () => {
    if (!fileToApprove) return;

    try {
      // Call API to approve formatted file and create voyages
      await XlsService.approveFormattedFile(vesselId, approveComments);

      console.log('Approving formatted file:', fileToApprove.fileName);
      console.log('Approval comments:', approveComments);

      ToastService.success(
        `Formatted file "${fileToApprove.fileName}" approved successfully and voyages are being created`,
        'File Approved'
      );

      // Reset dialog state
      setApproveDialogOpen(false);
      setApproveComments('');
      setFileToApprove(null);

      // Refresh the files list to show updated status
      await fetchUploadedFiles();
    } catch (error) {
      console.error('Failed to approve formatted file:', error);
      ToastService.handleApiError(error, 'File Approval');
    }
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setApproveComments('');
    setFileToApprove(null);
  };

  const validateAndSetFile = (file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const isValidType =
      validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (!isValidType) {
      ToastService.error('Please select a valid Excel file (.xlsx or .xls)', 'Invalid File Type');
      return;
    }

    // Validate file size (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      ToastService.error(
        `File size exceeds 10MB limit. Selected file: ${(file.size / 1024 / 1024).toFixed(2)} MB`,
        'File Too Large'
      );
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleUploadFormatted = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedFile) {
      ToastService.warning('Please select a file first', 'No File Selected');
      return;
    }

    if (!selectedRawFileId) {
      ToastService.warning('Please select an associated raw file', 'Raw File Required');
      return;
    }

    if (!user) {
      ToastService.error('User not authenticated', 'Authentication Error');
      return;
    }

    openUploadConfirmation(event.currentTarget);
  };

  const handleUploadFormattedConfirm = async () => {
    if (!selectedFile || !selectedRawFileId || !user) return;

    try {
      closeUploadConfirmation();
      setUploading(true);
      setShowProgress(true);
      setActiveStep(0);
      setUploadResult(null);

      // Call atomic upload API
      const response = await XlsService.uploadAndCreateVoyages(
        vesselId,
        vesselImo,
        vesselName,
        user.uid,
        selectedFile,
        selectedRawFileId
      );

      setUploadResult(response);

      if (response.success) {
        // Progress through steps on success
        setActiveStep(1); // File uploaded
        await new Promise((resolve) => setTimeout(resolve, 300));
        setActiveStep(2); // Parse & validate complete
        await new Promise((resolve) => setTimeout(resolve, 300));
        setActiveStep(3); // Voyages created (complete)

        ToastService.success(
          `File "${selectedFile.name}" processed successfully. ${response.created_voyage_ids?.length || 0} voyages created.`,
          'Upload Complete'
        );

        // Reset form but keep stepper visible
        setTimeout(() => {
          setSelectedFile(null);
          setSelectedRawFileId('');
          fetchUploadedFiles();
        }, 2000);
      } else {
        // Stop at the step where error occurred (use error_step from API)
        setActiveStep(response.error_step ?? DEFAULT_ERROR_STEP);
        // Don't show toast - error will be displayed in stepper section
      }
    } catch (error) {
      console.error('Failed to upload and create voyages:', error);

      // Check if error is ApiError with validation response data
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: AtomicUploadResponse };
        if (apiError.response) {
          // Use the actual API response with validation errors
          setUploadResult(apiError.response);
          setActiveStep(apiError.response.error_step ?? DEFAULT_ERROR_STEP); // Use error_step from API
          return;
        }
      }

      // Fallback for generic errors
      setUploadResult({
        success: false,
        error_message: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
      setActiveStep(DEFAULT_ERROR_STEP); // Default to validation step for generic errors
      // Don't show toast - error will be displayed in stepper section
    } finally {
      setUploading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'fileName',
      headerName: 'File Name',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'uploadedFileType',
      headerName: 'File Type',
      width: 150,
    },
    {
      field: 'fileSize',
      headerName: 'File Size',
      width: 120,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 170,
      renderCell: (params) => {
        const value = params.value as string | undefined;
        const display = value ? formatUTCForDisplay(value, false) : '-';
        return <Typography variant="body2">{display}</Typography>;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 170,
      renderCell: (params) => {
        const value = params.value as string | undefined;
        const display = value ? formatUTCForDisplay(value, false) : '-';
        return <Typography variant="body2">{display}</Typography>;
      },
    },
    {
      field: 'uploadedFrom',
      headerName: 'Uploaded By',
      width: 150,
      renderCell: (params) => {
        const uploadedFrom = params.value as number;
        return (
          <Typography variant="body2">
            {uploadedFrom === 1 ? 'Client' : uploadedFrom === 2 ? 'Admin' : '-'}
          </Typography>
        );
      },
    },
    {
      field: 'userDetails',
      headerName: 'User Details',
      width: 220,
      renderCell: (params) => {
        const file = params.row as UploadedFile;
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {file.uploadedByName || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {file.uploadedByEmail || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value.toUpperCase()}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'voyageProcessing',
      headerName: 'Voyages Processed',
      width: 190,
      renderCell: (params) => {
        const file = params.row as UploadedFile;
        const showCounts =
          (file.status === 'approved' || file.status === 'completed') && file.totalRows > 0;

        if (!showCounts) {
          return (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          );
        }

        return (
          <Box>
            <Typography variant="caption" display="block" color="text.secondary">
              Total: {file.totalRows}
            </Typography>
            <Typography variant="caption" display="block" color="success.main">
              Created: {file.successfulRows}
            </Typography>
            <Typography variant="caption" display="block" color="error.main">
              Failed: {file.failedRows}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const file = params.row as UploadedFile;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(file)}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.8125rem',
              }}
            >
              Download
            </Button>
            <IconButton
              aria-label="delete file"
              color="error"
              onClick={() => {
                handleDeleteClick(file);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      // Case 1: Formatted File
      if (fileToDelete.uploadedFileType === 'Formatted') {
        if (deleteOption === 'fileAndVoyages') {
          await XlsService.deleteFormattedFileAndVoyages(fileToDelete.id);
          ToastService.success('Successfully deleted file and its voyages.', 'Delete Complete');
        } else {
          await XlsService.deleteFormattedFile(fileToDelete.id);
          ToastService.success('Successfully deleted file.', 'Delete Complete');
        }
      }
      // Case 2: Raw File
      else {
        await XlsService.deleteRawFile(fileToDelete.id);
        ToastService.success('Successfully deleted raw file.', 'Delete Complete');
      }

      fetchUploadedFiles(); // Refresh the list
    } catch (err) {
      ToastService.handleApiError(err, 'Delete Failed');
    } finally {
      // Reset all state
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      setDeleteOption('');
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setUploadResult(null);
    setActiveStep(-1);
    setShowProgress(false);
  };

  const handleDeleteClick = (file: UploadedFile) => {
    setFileToDelete(file);
    setDeleteOption('');
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" component="div">
                Uploaded Files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vesselName} (IMO: {vesselImo})
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          {/* Upload Formatted File Section */}
          <Paper
            sx={{
              p: 1,
              mb: 3,
              backgroundColor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Upload Formatted File
            </Typography>

            {/* Raw File Selection Dropdown */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Select
                  value={selectedRawFileId}
                  onChange={(e) => setSelectedRawFileId(e.target.value)}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      py: 1.5,
                    },
                    backgroundColor: 'background.paper',
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Choose an Associated raw file
                        </Typography>
                      );
                    }
                    const file = uploadedFiles.find((f) => f.id === selected);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InsertDriveFileIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography sx={{ fontWeight: 500 }}>{file?.fileName}</Typography>
                      </Box>
                    );
                  }}
                >
                  {uploadedFiles
                    // Only show raw files that are not yet linked to a formatted upload.
                    // Raw files with a formattedUploadId have already been processed and
                    // associated to a formatted file, so excluding them here prevents
                    // reusing the same raw upload for multiple formatted uploads and
                    // keeps already-processed raw files out of this association list.
                    .filter((file) => file.uploadedFileType === 'Raw' && !file.formattedUploadId)
                    .map((file) => {
                      const isProcessed = file.processingStatus === 'processed';
                      const isFailed = file.processingStatus === 'failed';

                      return (
                        <MenuItem
                          key={file.id}
                          value={file.id}
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}
                          >
                            <InsertDriveFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {file.fileName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Uploaded: {file.uploadedAt}
                              </Typography>
                            </Box>
                            {isProcessed && (
                              <Chip
                                icon={<CheckCircleOutlineIcon />}
                                label="Processed"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 24 }}
                              />
                            )}
                            {isFailed && (
                              <Chip
                                icon={<ErrorOutlineIcon />}
                                label="Failed"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ height: 24 }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Box>

            {/* Drag and Drop Zone */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                backgroundColor: isDragging ? 'action.hover' : 'transparent',
                transition: 'all 0.2s',
                cursor: 'pointer',
                mb: 2,
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="file-upload"
                hidden
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 0.5 }} />
                <Typography variant="body2" gutterBottom>
                  Drag and drop your file here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accepted formats: .xlsx, .xls | Max size: 10MB
                </Typography>
              </label>
            </Box>

            {/* Selected File Display and Upload Button */}
            {selectedFile && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Chip
                  label={`${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`}
                  onDelete={handleClearFile}
                  color="primary"
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUploadFormatted}
                  disabled={uploading}
                  sx={{ textTransform: 'none' }}
                >
                  {uploading ? 'Uploading...' : 'Upload formatted'}
                </Button>
              </Box>
            )}

            {/* Upload Progress Stepper */}
            {showProgress && (
              <Paper
                sx={{ p: 3, mt: 2, backgroundColor: 'background.default', position: 'relative' }}
              >
                <IconButton
                  onClick={() => {
                    setShowProgress(false);
                    setActiveStep(-1);
                    setUploadResult(null);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label, index) => {
                    const isError = !!(
                      uploadResult &&
                      !uploadResult.success &&
                      index === activeStep
                    );
                    const isLoading = index === activeStep && uploading;

                    return (
                      <Step key={label}>
                        <StepLabel
                          error={isError}
                          StepIconComponent={
                            isLoading ? () => <CircularProgress size={24} /> : undefined
                          }
                          StepIconProps={{
                            sx: {
                              '&.Mui-active': {
                                color: isError ? 'error.main' : 'primary.main',
                              },
                              '&.Mui-completed': {
                                color: 'success.main',
                              },
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>

                {/* Show errors if any */}
                {uploadResult && !uploadResult.success && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <AlertTitle>
                        {uploadResult.validation_errors && uploadResult.validation_errors.length > 0
                          ? 'Validation Failed'
                          : 'Upload Failed'}
                      </AlertTitle>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {uploadResult.error_message ||
                          'Please check the errors below and try again.'}
                      </Typography>
                    </Alert>

                    {/* Show validation error tables if available */}
                    {uploadResult.validation_errors &&
                      uploadResult.validation_errors.length > 0 && (
                        <>
                          {/* Section 1: Column-level errors (error_type = 1) */}
                          {uploadResult.validation_errors.some((err) => err.error_type === 1) && (
                            <Box sx={{ mb: 3 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                Note: Voyages will be created only after all column-level
                                validations are resolved.
                              </Typography>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                ❌ Column level validation errors
                              </Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        '& th': { backgroundColor: '#E5EAF1', fontWeight: 600 },
                                      }}
                                    >
                                      <TableCell width={50}>S.No</TableCell>
                                      <TableCell>Column</TableCell>
                                      <TableCell>Error Description</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {uploadResult.validation_errors
                                      .filter((err) => err.error_type === 1)
                                      .map((error, index) => (
                                        <TableRow key={index}>
                                          <TableCell>{index + 1}</TableCell>
                                          <TableCell>{error.value}</TableCell>
                                          <TableCell>{error.message}</TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {/* Section 2: Row-level errors (error_type = 2) */}
                          {uploadResult.validation_errors.some((err) => err.error_type === 2) && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                Note: Voyages will be created only after all row-level validations
                                are resolved.
                              </Typography>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                ❌ Row level validation errors
                              </Typography>
                              <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{ maxHeight: 420, overflowY: 'auto' }}
                              >
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        '& th': { backgroundColor: '#E5EAF1', fontWeight: 600 },
                                      }}
                                    >
                                      <TableCell width={50}>S.No</TableCell>
                                      <TableCell>Row</TableCell>
                                      <TableCell>Column</TableCell>
                                      <TableCell>Value</TableCell>
                                      <TableCell>Error Description</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {uploadResult.validation_errors
                                      .filter((err) => err.error_type === 2)
                                      .map((error, index) => (
                                        <TableRow key={index}>
                                          <TableCell>{index + 1}</TableCell>
                                          <TableCell>{error.row || '-'}</TableCell>
                                          <TableCell>{error.field || '-'}</TableCell>
                                          <TableCell
                                            sx={{
                                              maxWidth: 200,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}
                                          >
                                            {error.value}
                                          </TableCell>
                                          <TableCell>{error.message}</TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </>
                      )}
                  </Box>
                )}

                {/* Show success summary (IDs omitted per request) */}
                {uploadResult && uploadResult.success && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <AlertTitle>Upload Successful!</AlertTitle>
                      <Typography variant="body2">
                        Successfully created {uploadResult.created_voyage_ids?.length || 0} voyages.
                      </Typography>
                      {uploadResult.upload_id && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          Upload ID: {uploadResult.upload_id}
                        </Typography>
                      )}
                    </Alert>
                  </Box>
                )}
              </Paper>
            )}
          </Paper>

          {/* Uploaded Files Table */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>NOTE:</strong> Only one formatted file can be uploaded for each raw file
            </Typography>
          </Alert>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            List of Files
          </Typography>
          {loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 200, width: '100%', mb: 2 }}>
              <DataGrid
                rows={uploadedFiles}
                columns={columns}
                disableRowSelectionOnClick
                hideFooter
                getRowHeight={() => 'auto'}
                sx={{
                  border: 0,
                  '& .MuiDataGrid-cell': {
                    padding: '8px',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unified Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
            <Typography variant="h6" component="div" fontWeight="medium">
              {fileToDelete?.uploadedFileType === 'Formatted'
                ? 'Delete Formatted File'
                : 'Delete Raw File'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Conditional Content */}
          {fileToDelete?.uploadedFileType === 'Formatted' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Are you sure you want to delete the file "{fileToDelete?.fileName}"? This action
                cannot be undone.
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup value={deleteOption} onChange={(e) => setDeleteOption(e.target.value)}>
                  <FormControlLabel value="fileOnly" control={<Radio />} label="Formatted file" />
                  <FormControlLabel
                    value="fileAndVoyages"
                    control={<Radio />}
                    label="Formatted file and its voyages"
                  />
                </RadioGroup>
              </FormControl>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to delete the file "{fileToDelete?.fileName}"? This action
              cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={
              // Disable only if it's a formatted file and no option is chosen
              fileToDelete?.uploadedFileType === 'Formatted' && !deleteOption
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Comments Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleApproveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Formatted File</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide comments before approving this file:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Comments"
            placeholder="Enter your approval comments..."
            value={approveComments}
            onChange={(e) => setApproveComments(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleApproveCancel}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveConfirm}
            variant="contained"
            color="success"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Confirmation Popover */}
      <ConfirmationPopover
        open={isUploadOpen}
        anchorEl={uploadAnchorEl}
        onClose={closeUploadConfirmation}
        onConfirm={handleUploadFormattedConfirm}
        title="Confirm Upload"
        message={
          <Box>
            <Typography>Are you sure you want to upload this file?</Typography>
            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Vessel:</strong> {vesselName}
              </Typography>
              <Typography variant="body2">
                <strong>File:</strong> {selectedFile?.name}
              </Typography>
            </Box>
          </Box>
        }
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={false}
      />
    </>
  );
};
